import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowRightLeftIcon,
  CheckCircle2Icon,
  CircleIcon,
  DatabaseIcon,
  Loader2Icon,
  PauseIcon,
  PlayIcon,
  ShieldAlertIcon,
} from 'lucide-react';
import {
  account,
  backup as backupApi,
  bridge,
  local,
  track,
  type BackupSnapshot,
  type BackupTypeState,
} from '@/lib/bridge';
import { describeError } from '@/lib/errorMessage';
import { useAccounts } from '@/lib/useAccounts';
import { useAppInfo } from '@/lib/useAppInfo';
import { useAppReady } from '@/lib/useAppReady';
import { AccountCard } from '@/components/AccountCard';
import { PageShell } from '@/components/PageShell';
import { SelectRow } from '@/components/SelectRow';
import { TaskProgressCard, taskStatusLabel } from '@/components/task-ui';
import { PageSkeleton } from '@/components/skeletons';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CARD } from '@/components/ui';

/** 搬小音备份类型（与原生 BackupType 对齐）。 */
const TYPES = [
  { key: 'posts', label: '作品' },
  { key: 'likes', label: '点赞' },
  { key: 'collects', label: '收藏夹' },
  { key: 'collectedVideos', label: '收藏视频' },
  { key: 'following', label: '关注' },
];

/** 备份页自己的状态措辞（照搬搬小书的 overrides 写法）。 */
const STATUS_LABELS: Record<string, string> = {
  running: '备份中',
};

/** 逐类型状态文案（照搬搬小书 StatusIcon 的语义）。 */
const TYPE_STATUS_TEXT: Record<string, string> = {
  idle: '等待中',
  running: '备份中',
  paused: '已暂停',
  completed: '已完成',
  failed: '失败',
};

/** 备份中拿勾选框当状态图标会看不出是「已选」还是「跑完了」，用独立图标区分。 */
function StatusIcon({ status }: { status: string }) {
  const box = 'flex h-4 w-4 shrink-0 items-center justify-center';
  if (status === 'completed') {
    return (
      <span className={box}>
        <CheckCircle2Icon className="text-primary size-4" />
      </span>
    );
  }
  if (status === 'running') {
    return (
      <span className={box}>
        <Loader2Icon className="text-primary size-4 animate-spin" />
      </span>
    );
  }
  if (status === 'paused') {
    return (
      <span className={box}>
        <PauseIcon className="text-muted-foreground size-4" />
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span className={box}>
        <ShieldAlertIcon className="text-destructive size-4" />
      </span>
    );
  }
  return (
    <span className={box}>
      <CircleIcon className="text-muted-foreground/40 size-4" />
    </span>
  );
}

export function BackupPage() {
  const { loading: infoLoading, isNative } = useAppInfo();
  const { accounts, selectedId, setSelectedId, error: accountError, loading: accountsLoading } = useAccounts();
  const [types, setTypes] = useState<string[]>(['posts', 'likes', 'collects']);
  const [snapshot, setSnapshot] = useState<BackupSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** 各类型「本地已备份条数」（native local.counts）。 */
  const [counts, setCounts] = useState<Record<string, number>>({});

  const loaded = !infoLoading && !accountsLoading;
  useAppReady(loaded);

  // 埋点：页面访问
  useEffect(() => {
    track.pageOpen('backup');
  }, []);

  // 进度用事件推送，不轮询（PROTOCOL.md §5）
  useEffect(() => {
    const off = bridge.on('backup.progress', (payload) => setSnapshot(payload as BackupSnapshot));
    return off;
  }, []);

  const currentAccount = accounts.find((a) => a.id === selectedId) ?? null;

  // 进页面先拉一次初始状态（搬小书同款：状态卡与操作区由 state 驱动）
  useEffect(() => {
    if (!currentAccount) return;
    let cancelled = false;
    backupApi
      .status(currentAccount.id)
      .then((next) => {
        if (!cancelled) setSnapshot((prev) => prev ?? next);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [currentAccount?.id]);

  const state = snapshot;

  // 埋点：备份完成 / 失败 / 暂停（只在状态跳转时各发一次）
  const prevBackupStateRef = useRef<string | null>(null);
  useEffect(() => {
    const current = state?.state ?? null;
    const prev = prevBackupStateRef.current;
    prevBackupStateRef.current = current;
    if (prev == null || current === prev) return;
    if (current === 'completed') track.event('backup_complete');
    else if (current === 'failed') track.event('backup_fail');
    else if (current === 'paused') track.event('backup_pause');
  }, [state?.state]);

  // 注意：搬小书的 `isActiveTaskStatus` 把 idle 也算「进行中」（他们的后端
  // idle = 任务已建未跑）。我们这边的 idle 只是「没有在跑的任务」，
  // 所以只认 running / paused。
  const active = state?.state === 'running' || state?.state === 'paused';
  const writableCount = accounts.filter((a) => a.canWrite).length;
  const canWrite = currentAccount?.canWrite ?? false;

  // 本地已备份条数：进页面 / 切账号 / 一次备份跑完后刷新
  const refreshCounts = useCallback(() => {
    if (!currentAccount) return;
    local
      .counts(currentAccount.id)
      .then(setCounts)
      .catch(() => {});
  }, [currentAccount?.id]);

  useEffect(() => {
    refreshCounts();
  }, [refreshCounts]);

  useEffect(() => {
    if (state?.state === 'completed') refreshCounts();
  }, [state?.state, refreshCounts]);

  const switchAccount = useCallback(async () => {
    try {
      setSelectedId((await account.pick('authenticated')).id);
    } catch {
      // 用户取消：保留当前账号
    }
  }, [setSelectedId]);

  const run = useCallback(
    async (action: 'start' | 'pause' | 'resume') => {
      if (!currentAccount || busy) return;
      setBusy(true);
      setError(null);
      if (action === 'start') track.event('backup_start', { types });
      try {
        const next =
          action === 'start'
            ? await backupApi.start(currentAccount.id, types)
            : action === 'pause'
              ? await backupApi.pause(currentAccount.id)
              : await backupApi.resume(currentAccount.id);
        setSnapshot(next);
      } catch (e) {
        setError(describeError(e));
      } finally {
        setBusy(false);
      }
    },
    [busy, currentAccount, types],
  );

  const toggleType = useCallback(
    (key: string) => {
      if (busy || active) return;
      setTypes((prev) => {
        const next = prev.includes(key) ? prev.filter((t) => t !== key) : [...prev, key];
        if (!next.length) {
          setError('至少保留一种备份数据');
          return prev;
        }
        setError(null);
        return next;
      });
    },
    [active, busy],
  );

  /** 某类型的 progress 快照（可能还没有：未选的类型不出现）。 */
  const typeStateOf = (key: string): BackupTypeState | undefined =>
    state?.types?.find((t) => t.type === key);

  // 已完成/失败的类型数，用于「已完成 N / M 项」
  const planned = types.length;
  const done = types.filter((k) => {
    const st = typeStateOf(k)?.status;
    return st === 'completed' || st === 'failed';
  }).length;

  return (
    <PageShell title="备份控制台" description="搬小音 · 由原生 BridgeWebView 承载" backTo="/">

      {currentAccount ? (
        <AccountCard
          account={currentAccount}
          label="备份账号"
          detail={writableCount > 1 ? `${writableCount} 个账号可切换` : undefined}
          onClick={writableCount > 1 ? () => void switchAccount() : undefined}
        />
      ) : null}

      {!isNative && loaded ? (
        <Card className={CARD}>
          <p className="text-muted-foreground text-xs">
            当前不在 App 内。浏览器里单独调试请加 <code>?mock=1</code>。
          </p>
        </Card>
      ) : null}

      {loaded && !currentAccount ? (
        <InfoBlock
          title="没有可备份的账号"
          detail="备份需要至少一个账号，请先在 App 内登录后再来。"
        />
      ) : null}

      {currentAccount ? (
        <>
          {active && state ? (
            <TaskProgressCard
              title={taskStatusLabel(state.state, STATUS_LABELS) ?? state.state}
              right={active ? `已完成 ${done} / ${planned} 项` : undefined}
              ratio={state.progress}
              paused={state.state === 'paused'}
              action={
                <div className="text-muted-foreground flex flex-wrap gap-x-3.5 gap-y-0.5 text-xs">
                  <span>
                    {state.state === 'paused'
                      ? '已暂停，点「继续备份」接着跑'
                      : state.type
                        ? `正在备份：${TYPES.find((t) => t.key === state.type)?.label ?? state.type}`
                        : '准备中…'}
                  </span>
                </div>
              }
            />
          ) : null}

          {state?.state === 'completed' ? (
            <TaskProgressCard
              finished
              bar={false}
              title="备份完成"
              detail={state.processed != null ? `已处理 ${state.processed} 条` : undefined}
            />
          ) : null}

          {state?.state === 'failed' ? (
            <TaskProgressCard
              bar={false}
              icon={<ShieldAlertIcon className="text-destructive size-5 shrink-0" />}
              title={taskStatusLabel(state.state) ?? '备份失败'}
              detail={state.error ? describeError(state.error) : '可重新开始备份'}
            />
          ) : null}

          <Card className={CARD}>
            <p className="text-muted-foreground text-xs">
              {active
                ? '备份进行中，完成后可看到本次结果。'
                : '选择要备份的数据。增量只拉新数据，已备份的不重复拉。'}
            </p>
            {TYPES.map((def) => {
              const row = typeStateOf(def.key);
              const status = row?.status ?? 'idle';
              const selected = types.includes(def.key);
              const processed = row?.processed ?? 0;
              // 运行中的类型右侧显示实时已备份条数；其余显示本地已备份总数。
              const live = active && selected && status === 'running';
              const count = live ? processed : (counts[def.key] ?? 0);
              return (
                <SelectRow
                  key={def.key}
                  checked={selected}
                  onCheckedChange={() => toggleType(def.key)}
                  disabled={active || !canWrite}
                  icon={active ? <StatusIcon status={status} /> : undefined}
                  detail={`${count} 条`}
                  footer={active && selected ? (TYPE_STATUS_TEXT[status] ?? status) : undefined}
                >
                  {def.label}
                </SelectRow>
              );
            })}
            {!canWrite ? (
              <p className="text-muted-foreground text-xs leading-snug">
                该账号未登录或登录已过期，请先在 App 内重新登录后再备份。
              </p>
            ) : null}
          </Card>

          <Card className={CARD}>
            {!active && state?.state !== 'completed' ? (
              <div className="flex flex-col gap-2">
                <Button
                  className="w-full"
                  loading={busy}
                  disabled={types.length === 0 || !canWrite}
                  onClick={() => void run('start')}
                >
                  {!busy ? <DatabaseIcon data-icon="inline-start" /> : null}
                  {busy ? '处理中…' : '开始备份'}
                </Button>
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => (window.location.href = '/bxy/migration')}
                >
                  <ArrowRightLeftIcon data-icon="inline-start" />
                  迁移数据
                </Button>
              </div>
            ) : null}
            {!active && state?.state === 'completed' ? (
              <Button className="w-full" onClick={() => (window.location.href = '/bxy/migration')}>
                开始迁移
              </Button>
            ) : null}
            {state?.state === 'running' ? (
              <Button
                variant="secondary"
                className="w-full"
                loading={busy}
                onClick={() => void run('pause')}
              >
                <PauseIcon data-icon="inline-start" />
                暂停
              </Button>
            ) : null}
            {state?.state === 'paused' || state?.state === 'failed' ? (
              <Button className="w-full" loading={busy} onClick={() => void run('resume')}>
                <PlayIcon data-icon="inline-start" />
                继续备份
              </Button>
            ) : null}
          </Card>
        </>
      ) : null}

      {error ?? accountError ? (
        <p className="text-destructive px-1 text-sm">{error ?? accountError}</p>
      ) : null}

      {!loaded ? <PageSkeleton kind="backup" rows={3} /> : null}
    </PageShell>
  );
}

/** 与搬小书一致的「没有可用账号」提示块。 */
function InfoBlock({ title, detail }: { title: string; detail: string }) {
  return (
    <Card className={CARD}>
      <div className="flex items-center gap-2">
        <ShieldAlertIcon className="text-primary size-5 shrink-0" />
        <strong className="text-sm font-semibold">{title}</strong>
      </div>
      <p className="text-muted-foreground mt-1 text-xs">{detail}</p>
    </Card>
  );
}
